import { useCallback, useState } from "react";
import { BasketKPLabels, TBasketData, TBasketPackage } from "../BasketKP.types";
import ConfirmModal from "shared/ConfirmModal";
import axios from "axios";
import * as R from "ramda";
import { useMedia } from "hooks";

// Renders a gift voucher article in the basket. Visually distinct from a
// normal package: no quantity counter (qty is fixed by the BE, always shown
// as-is and not editable), a Recipient column instead of price-per-person /
// no-of-guests, but the same delete flow as BasketItem.
function VoucherItem({
  labels,
  basketPackage,
  renderPrice,
  updateBasketData,
  domain,
  basketItemIndex,
  performanceGroupIndex,
  basketData,
  setIsLoading,
}: {
  labels: BasketKPLabels;
  basketPackage: TBasketPackage;
  renderPrice: (price: number | undefined) => string;
  updateBasketData: React.Dispatch<React.SetStateAction<TBasketData | null>>;
  domain: string;
  basketItemIndex: number;
  performanceGroupIndex: number;
  basketData: TBasketData;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const { isTablet } = useMedia();

  // Same mechanism BasketItem uses: rebuild the full products payload from the
  // current basket data and POST it to updatebasket. Isolated here so the
  // voucher remove path can diverge later (e.g. dedicated endpoint) in one spot.
  const updateBasketProduct = useCallback(
    (dataToUpdate: TBasketData) => {
      const products = dataToUpdate?.PerformanceGroups.flatMap(
        (item) => item.BasketPackagesAndExtras,
      );

      const dataToSend = products.map((product) => ({
        ...product,
        OptionalExtras: product.OptionalExtrasGroups?.flatMap((opt) =>
          opt.OptionalExtras.flatMap((oo) => oo.Items),
        ),
      }));

      setIsLoading(true);
      axios
        .request({
          data: { Products: dataToSend },
          method: "POST",
          url: domain + `/api/sitecore/SecutixCheckout/updatebasket`,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          withCredentials: true,
        })
        .then((res) => {
          if (res.data.BasketId) {
            updateBasketData(res.data ?? { ...dataToUpdate, ...res.data });

            const basketHeaderWrapper =
              document.getElementsByClassName("icon-basket");
            if (basketHeaderWrapper && basketHeaderWrapper[0]) {
              if (!basketHeaderWrapper[0].classList.contains("active")) {
                basketHeaderWrapper[0].classList.add("active");
              }
            }

            const basketHeaderElem = document.querySelectorAll(
              '[data-basketproperty="TotalItemsCount"]',
            );
            if (basketHeaderElem) {
              basketHeaderElem.forEach((elem) => {
                elem.textContent = res?.data?.TotalItems;
              });
            }
          }
        })
        .finally(() => setIsLoading(false));
    },
    [domain, setIsLoading, updateBasketData],
  );

  const removeVoucher = useCallback(() => {
    updateBasketProduct(
      R.modifyPath(
        [
          "PerformanceGroups",
          performanceGroupIndex,
          "BasketPackagesAndExtras",
          basketItemIndex,
          "BasketQuantity",
        ],
        () => 0,
        basketData,
      ),
    );
  }, [basketData, basketItemIndex, performanceGroupIndex, updateBasketProduct]);

  return (
    <>
      <div
        className="o-basket__package"
        style={{ gridTemplateColumns: "2.2fr 1px 2.6fr 1fr 1fr 1px 0.5fr" }}
      >
        <div className="o-basket__package__info">
          <span>{labels.PackageText}</span>
          <span>{basketPackage.ProductName}</span>
        </div>
        <div className="o-basket__divider"></div>
        <div className="o-basket__package__info">
          {/* Override the column's default right-align (CSS targets non-first
              columns) so the recipient block reads left-aligned per design. */}
          <span style={{ textAlign: "left" }}>Recipient</span>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              textAlign: "left",
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: "18px", lineHeight: "120%" }}>
              {basketPackage.Recipient}
            </span>
            {basketPackage.Delivery && (
              <span style={{ fontSize: "11px", lineHeight: "140%" }}>
                {`Delivery: ${basketPackage.Delivery}`}
              </span>
            )}
          </div>
        </div>
        {/* Center the single Qty column in the white box: the shared
            .o-basket__package__quantity uses space-between for the normal
            two-column (qty + guests) layout, which left-pins our single column. */}
        <div
          className="o-basket__package__quantity"
          style={{ justifyContent: "center" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              justifyContent: "space-between",
              alignItems: isTablet ? "stretch" : "center",
              width: isTablet ? "100%" : undefined,
            }}
          >
            <span style={{ fontSize: "14px", textAlign: "center" }}>Qty</span>
            {/* Locked quantity: the BE controls voucher qty, user cannot change
                it. On mobile the input stretches full width per design. */}
            <div
              className="o-basket__package__quantity-input"
              style={{ width: isTablet ? "100%" : undefined }}
            >
              <div style={{ width: isTablet ? "100%" : undefined }}>
                {basketPackage.BasketQuantity}
              </div>
            </div>
          </div>
        </div>
        <div className="o-basket__package__info o-basket__package__info--mobile">
          <span>{labels.TotalText}</span>
          <span>
            {renderPrice(
              basketPackage.PriceWithVat * basketPackage.BasketQuantity,
            )}
          </span>
        </div>
        <div className="o-basket__divider hide-mobile"></div>
        <div
          className="o-basket__package__remove-btn"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <button
            onClick={() => setIsConfirmOpen(true)}
            className="a-button remove-btn"
            title={labels.RemoveButtonText}
            aria-label={labels.RemoveButtonText}
          >
            <i className="zmdi zmdi-delete" style={{ fontSize: "24px" }}></i>
          </button>
        </div>
      </div>
      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          removeVoucher();
          setIsConfirmOpen(false);
        }}
        title={labels.WarningModalTitle}
        message={labels.RemoveItemQuestionText}
        confirmText={labels.RemoveButtonText}
        cancelText={labels.RemoveItemCancelText}
      />
    </>
  );
}

export default VoucherItem;
